"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileSearch, Wrench, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface MethodologyStep {
  stepNumber: string;
  title: string;
  subtitle: string;
  description: string;
  iconName: "FileSearch" | "Wrench" | "MapPin" | "Star" | "Code2" | "BarChart3";
  deliverable: string;
}
const stepsData: MethodologyStep[] = [{
  stepNumber: "01",
  title: "Technical & On-Page Audit",
  subtitle: "Diagnosis & Discovery",
  description: "I perform a granular, hands-on audit of your site architecture, crawl health, and keyword gaps to identify immediate low-hanging growth opportunities.",
  iconName: "FileSearch",
  deliverable: "Actionable SEO Roadmap & Technical Backlog"
}, {
  stepNumber: "02",
  title: "Structural Optimization",
  subtitle: "Foundation & Performance",
  description: "I directly execute technical enhancements, core web vitals optimization, and copy restructuring to maximize search engine indexing and user engagement.",
  iconName: "Wrench",
  deliverable: "Refined On-Page Assets & High-Speed Architecture"
}, {
  stepNumber: "03",
  title: "Citations & Local Prominence",
  subtitle: "Regional Authority Building",
  description: "For national and regional target locations, I strengthen local citations, directory consistency, and NAP data to dominate localized search intent.",
  iconName: "MapPin",
  deliverable: "Verified Citation Network & GBP Optimization"
}, {
  stepNumber: "04",
  title: "Reviews & Reputation Trust",
  subtitle: "Authentic Social Proof",
  description: "I help establish clear customer feedback pipelines and schema-backed review structures that boost organic conversion rates and search trust.",
  iconName: "Star",
  deliverable: "Review Acquisition Strategy & Conversion Triggers"
}, {
  stepNumber: "05",
  title: "Advanced Schema & Entities",
  subtitle: "Structured Data Integration",
  description: "I write and deploy custom JSON-LD entity markup so search engines and emerging AI answer engines understand your brand with zero ambiguity.",
  iconName: "Code2",
  deliverable: "Custom Entity Schema & AI Visibility Tags"
}, {
  stepNumber: "06",
  title: "Transparent Reporting",
  subtitle: "Continuous Strategy Growth",
  description: "You receive direct video walkthroughs and live analytics dashboards detailing true commercial impact, leads generated, and organic growth.",
  iconName: "BarChart3",
  deliverable: "No-Fluff KPI Dashboard & Direct Monthly Review"
}];
export default function MethodologyFlow() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: MethodologyStep["iconName"]) => {
    switch (name) {
      case "FileSearch":
        return <FileSearch className="size-6 text-primary-foreground" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary-foreground" />;
      case "MapPin":
        return <MapPin className="size-6 text-primary-foreground" />;
      case "Star":
        return <Star className="size-6 text-primary-foreground" />;
      case "Code2":
        return <Code2 className="size-6 text-primary-foreground" />;
      case "BarChart3":
        return <BarChart3 className="size-6 text-primary-foreground" />;
      default:
        return <FileSearch className="size-6 text-primary-foreground" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologyflow" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-0 -translate-x-1/2 size-96 bg-secondary/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 translate-x-1/2 size-96 bg-accent/15 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider border border-accent/20">
            <Sparkles className="size-3.5 mr-1.5 text-accent" />
            Hands-On SEO Process
          </Badge>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-5">
            A Proven Step-by-Step SEO Methodology
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
            When you hire YE Consultancy, you work directly with me—Yassine Essaadi. My structured 6-step framework transforms technical complexity into measurable search visibility and sustainable commercial revenue.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 relative">
          {stepsData.map((step, index) => {
          const isLastInRowDesktop = (index + 1) % 3 === 0;
          const isLastInRowTablet = (index + 1) % 2 === 0;
          return <motion.div key={step.stepNumber} data-index={index} variants={itemVariants} className="relative group flex flex-col h-full">
                {!isLastInRowDesktop && index < stepsData.length - 1 && <div className="hidden lg:block absolute top-12 -right-4 w-8 h-0.5 bg-secondary z-0 transition-colors group-hover:bg-accent" />}

                {!isLastInRowTablet && index < stepsData.length - 1 && <div className="hidden md:block lg:hidden absolute top-12 -right-3 w-6 h-0.5 bg-secondary z-0 transition-colors group-hover:bg-accent" />}

                <Card className="flex-1 bg-card border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40 flex flex-col justify-between relative z-10 overflow-hidden">
                  <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-secondary opacity-80" />

                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shadow-sm">
                        {getIcon(step.iconName)}
                      </div>

                      <span className="text-xs font-semibold px-3 py-1 rounded-full bg-muted text-muted-foreground font-mono">
                        STEP {step.stepNumber}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <span className="text-xs font-medium text-accent uppercase tracking-wider block">
                        {step.subtitle}
                      </span>
                      <CardTitle className="text-xl font-semibold text-foreground font-default">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6 flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>

                    <div className="pt-3 border-t border-border/60">
                      <div className="bg-secondary/40 rounded-lg p-3 border border-secondary flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <div className="text-xs">
                          <span className="font-semibold text-foreground block mb-0.5">Key Deliverable:</span>
                          <span className="text-muted-foreground">{step.deliverable}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 md:mt-20 rounded-2xl bg-muted/60 border border-border p-8 md:p-10 text-center max-w-4xl mx-auto shadow-sm">
          <div className="max-w-2xl mx-auto space-y-4">
            <h3 className="text-2xl font-semibold text-foreground font-default">
              Ready to execute a tailored strategy directly with Yassine?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              No account managers, no middle-tier agency staff. Every audit, optimization, and report is built directly by me.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="w-full sm:w-auto px-6 font-medium" asChild>
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="w-full sm:w-auto px-6 font-medium" asChild>
                <Link to="/CaseStudiesPage">
                  Explore Verified Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}